import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { smartGovernanceOff, smartGovernanceOn } from 'kosmos/images';
import { CompanyScoringState, OrganisationAssignmentDto } from 'models/dtoTypes';
import { ESmartGovernanceStatus } from '../SmartGovernanceWidget';
import { SmartGovernanceAssignmentAttributes } from 'kosmos/models';
import CreateResourceModal, { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { get } from 'lodash';
import { CreateOrganisationAssignment } from 'services/organisationAssignment';
import Notice from '../Notice';
import axios from 'axios';
import { CurrentUser } from 'auth';
import { EFeature } from 'auth/Authentication';

const PREFIX = '_smartGovernanceVerificationLogo';

const classes = {
  content: `${PREFIX}-content`,

  column: `${PREFIX}-column`,
  icon: `${PREFIX}-icon`,
};

const Root = styled('div')(() => ({
  float: 'right',
  [`& .${classes.content}`]: {
    position: 'relative',
    width: 235,
    display: 'flex',
    cursor: 'pointer',
    [`& .${classes.column}`]: {
      flex: 1,
      paddingLeft: 3,
      paddingRight: 3,
    },
    [`& .${classes.icon}`]: {
      border: '1px solid #3543d0',
      borderRadius: 4,
      width: '100%',
      padding: 3,
    },
    [`&.inactive .${classes.icon}`]: {
      border: '1px solid #949494',
      borderRadius: 4,
      width: '100%',
      padding: 3,
    },
    ['&.inactive h6']: {
      color: '#949494',
    },
  },
}));

interface IProps {
  kosmosCompanyId: string;
  smartGovernanceAssignment?: OrganisationAssignmentDto;
  onClickCompleted?: (id: string) => void;
  align: 'left' | 'right';
}

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function SmartGovernanceVerificationLogo(props: IProps): JSX.Element {
  const { align, smartGovernanceAssignment, kosmosCompanyId } = props;
  // only user can assign sg can have onClick function
  const disabled = !CurrentUser.getInstance().canManageResource(EFeature.assignSg);
  // once sent to sme, it should be active
  const active = Boolean(smartGovernanceAssignment?.manualCompanyId);
  const [openCreate, setOpenCreate] = useState(false);
  const [noticeData, setNoticeData] = useState<INoticeData>();
  const [smartGovernanceStatus, setSmartGovernanceStatus] = useState<ESmartGovernanceStatus>();

  const renderIcon = () => {
    const src = active ? smartGovernanceOn : smartGovernanceOff;
    return (
      <img src={src} className={classes.icon}/>
    );
  };

  useEffect(() => {
    setSmartGovernanceStatus(getSmartGovernanceStatus());
  }, [smartGovernanceAssignment]);

  const getSmartGovernanceStatus = (): ESmartGovernanceStatus => {
    if (!smartGovernanceAssignment) {
      return ESmartGovernanceStatus.notStarted;
    } else if (!smartGovernanceAssignment.isAccepted) {
      return ESmartGovernanceStatus.sent;
    } else if (!smartGovernanceAssignment.manualCompanyId) {
      return ESmartGovernanceStatus.assignError;
    } else {
      const scoringState = smartGovernanceAssignment?.manualCompany?.scoringState;
      if (scoringState === CompanyScoringState.Completed) {
        return ESmartGovernanceStatus.completed;
      } else if (scoringState === CompanyScoringState.InProgress) {
        return ESmartGovernanceStatus.inProgress;
      } else if (scoringState === CompanyScoringState.Error) {
        return ESmartGovernanceStatus.scoreError;
      }
      return ESmartGovernanceStatus.accepted;
    }
  };

  const handleSelectSgCompany = () => {
    if (disabled) {
      return;
    }
    const status = smartGovernanceStatus;
    if (status === ESmartGovernanceStatus.notStarted) {
      setOpenCreate(true);
      return;
    } else if (status === ESmartGovernanceStatus.completed) {
      window.open(`/combined-reports/${kosmosCompanyId}`, '_blank');
      return;
    }
  };

  const renderText = () => {
    const style = {
      marginTop: 5,
      textAlign: align,
    };

    return (
      <div style={style}>
        <Typography
          variant="h6"
          color="secondary"
          sx={{ fontWeight: 'bold', lineHeight: 1.1 }}
        >
          SMART
        </Typography>
        <Typography
          variant="h6"
          color="secondary"
          sx={{ fontWeight: 'bold', lineHeight: 1.1 }}
        >
          GOVERNANCE
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 'bold', lineHeight: 1.1, textTransform: 'uppercase' }}
        >
          {smartGovernanceStatus}
        </Typography>
      </div>
    );
  };

  const schema: ISchema = {
    fields: [
      {
        label: 'Assign To',
        name: 'assigneeId',
        required: true,
        widget: 'smeUserSelector',
        description: ''
      },
    ]
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      // native error
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCreate = async (attrs: SmartGovernanceAssignmentAttributes) => {
    console.log(attrs);
    try {
      await CreateOrganisationAssignment({
        kosmosCompanyId: kosmosCompanyId,
        assigneeId: get(attrs, 'assigneeId') || '',
      });
      setSmartGovernanceStatus(ESmartGovernanceStatus.sent);
      setOpenCreate(false);
      setNoticeData({
        message: 'Successfully created.',
        severity: 'success',
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);

  };

  return (
    <Root
    >
      <div
        className={`${classes.content} ${active ? 'active' : 'inactive'}`}
        onClick={handleSelectSgCompany}
      >
        {align === 'right' && (
          <>
            <div className={classes.column}>
              {renderText()}
            </div>
            <div className={classes.column}>
              {renderIcon()}
            </div>
          </>
        ) || (
          <>
            <div className={classes.column}>
              {renderIcon()}
            </div>
            <div className={classes.column}>
              {renderText()}
            </div>
          </>
        )}
      </div>
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
      <CreateResourceModal<SmartGovernanceAssignmentAttributes>
        open={openCreate}
        label="Assign Smart Governance"
        schema={schema}
        onClose={() => setOpenCreate(false)}
        onSave={handleCreate}
      />
    </Root>
  );
}

